import api from './api';
import { Course } from '../types';

class CourseService {
  async getCourses(container?: string | number | null): Promise<Course[]> {
    const params: { container?: string | number } = {};
    if (container !== undefined) {
      params.container = container === null ? 'null' : container;
    }
    const response = await api.get('/courses/', { params });
    // Handle both array and paginated response
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data.results) {
      return response.data.results;
    }
    return [];
  }

  async getCourse(id: number): Promise<Course> {
    const response = await api.get<Course>(`/courses/${id}/`);
    return response.data;
  }

  async createCourse(data: Partial<Course>): Promise<Course> {
    const response = await api.post<Course>('/courses/', data);
    return response.data;
  }

  async updateCourse(id: number, data: Partial<Course>): Promise<Course> {
    // Use PATCH for partial updates (especially for public courses)
    const response = await api.patch<Course>(`/courses/${id}/`, data);
    return response.data;
  }

  async deleteCourse(id: number, keepPublic: boolean = false): Promise<void> {
    await api.delete(`/courses/${id}/`, {
      data: { keep_public: keepPublic }
    });
  }

  async uploadImage(courseId: number, file: File, name: string): Promise<any> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('name', name);
    formData.append('course', courseId.toString());

    const response = await api.post(`/course-images/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getAvailableCourses(): Promise<Course[]> {
    const response = await api.get('/courses/available/');
    return response.data;
  }

  async adoptCourse(courseId: number): Promise<Course> {
    const response = await api.post<Course>(`/courses/${courseId}/adopt/`);
    return response.data;
  }

  async duplicateCourse(
    courseId: number,
    options?: {
      container?: number | null;
      copy_quick_recap_course?: boolean;
      quick_recap_course_container?: number | null;
      copy_quick_recap_video?: boolean;
      quick_recap_video_container?: number | null;
    }
  ): Promise<Course> {
    const response = await api.post<Course>(`/courses/${courseId}/duplicate/`, options || {});
    return response.data;
  }

  async reorderCourses(items: {id: number, order: number}[]): Promise<void> {
    await api.post('/courses/reorder/', { items });
  }

  async setThumbnail(courseId: number, selection: { page: number; x: number; y: number; width: number; height: number }): Promise<any> {
    const response = await api.post(`/courses/${courseId}/set_thumbnail/`, selection);
    return response.data;
  }

  async assistWithAi(prompt: string): Promise<{ content: string }> {
    const response = await api.post('/courses/ai_assist/', { prompt });
    return response.data;
  }

  async addLibraryImage(courseId: number, imageId: number): Promise<Course> {
    const response = await api.post(`/courses/${courseId}/add_library_image/`, { image_id: imageId });
    return response.data.course;
  }

  async removeLibraryImage(courseId: number, imageId: number): Promise<Course> {
    const response = await api.post(`/courses/${courseId}/remove_library_image/`, { image_id: imageId });
    return response.data.course;
  }
}

const courseService = new CourseService();
export default courseService;